import React, { FunctionComponent, Suspense, useState } from 'react';
import { graphql } from 'react-relay';
import { useLazyLoadQuery } from 'react-relay/hooks';
import { CustomErrorBoundary } from 'src/components/widgets/customErrorBoundary';
import { NotificationBanner } from 'src/components/widgets/notificationBanner';
import { Section } from 'src/components/widgets/section';
import { T } from 'src/shared/helpers';
import { RelayFragment } from 'src/shared/types';
import styled from 'styled-components';
import { Checkbox } from '../../../widgets/checkbox';
import { CatalogListLoaderContent } from '../../../widgets/catalogList/catalogListItemLoader';
import { SearchInput } from '../../../widgets/input';
import { ContainerBase } from '../../../widgets/layouts';
import { ResourceListViewModeSwitch } from '../../../widgets/resourceList';
import { merchantProduct_item$data } from '../state/queries/__generated__/merchantProduct_item.graphql';
import { storeCatalogQuery } from './__generated__/storeCatalogQuery.graphql';
import StoreCatalogPaginationFragment from './storeCatalogPaginationFragment';

import { SectionContent } from '../../../widgets/section/sectionContent';
import { SectionDescription } from '../../../widgets/section/sectionDescription';
import { SectionHeader } from '../../../widgets/section/sectionHeader';

const CompactSection = styled(Section)`
    flex: 1;
    min-height: 0;
    ${SectionDescription} {
        margin-bottom: 0 !important;
    }
    ${SectionHeader} {
        padding: 6px 24px 0;
    }
    ${SectionContent} {
        padding-top: 18px;
        min-height: 0;
    }
`;

// Fills the dialog/page height so the catalog (and its empty state) reaches the bottom.
const CatalogContainer = styled(ContainerBase)`
    display: flex;
    flex-direction: column;
    min-height: 100%;
`;

const SearchRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    > *:first-child {
        flex: 1;
        min-width: 0;
    }
`;

const FilterDivider = styled.div`
    width: 1px;
    height: 20px;
    background: #cfd8dc;
    flex-shrink: 0;
`;

const SmallCheckbox = styled.div`
    flex-shrink: 0;

    label {
        font-size: 13px !important;
    }
`;

// Standalone "hide already configured" filter shown when publishing (the full search row is hidden there).
const PublishingCheckboxRow = styled.div`
    margin-bottom: 12px;

    label {
        font-size: 13px !important;
    }
`;

// Parent page
const StoreCatalog: FunctionComponent<{
    salesChannelID: string;
    scrollContainer?: HTMLDivElement | null;
    selectedProductsSKUs?: string[];
    showSelectionCheckboxes?: boolean;
    cardTitle?: string;
    cardTitleHtml?: React.ReactNode;
    onProductClick: (product: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>) => void;
    showHeader?: boolean;
    showBackButton?: boolean;
    hideAlreadyConfiguredBadge?: boolean;
    hideProductsNotConfigured?: boolean;
    disableProductCode?: string;
    searchProduct?: string;
    isAddSalesChannel?: boolean;
    isPublishingProduct?: boolean;
    isForImportingVariants?: boolean;
    hideConfigured?: boolean;
    onHideConfiguredChange?: (checked: boolean) => void;
}> = ({
    scrollContainer,
    selectedProductsSKUs,
    showSelectionCheckboxes = false,
    onProductClick,
    salesChannelID,
    cardTitle,
    cardTitleHtml,
    showHeader,
    showBackButton,
    hideAlreadyConfiguredBadge = false,
    hideProductsNotConfigured = false,
    disableProductCode,
    searchProduct = '',
    isAddSalesChannel,
    isPublishingProduct,
    isForImportingVariants,
    hideConfigured: hideConfiguredProp,
    onHideConfiguredChange
}) => {
    const [search, setSearch] = useState('');
    const [internalHideConfigured, setInternalHideConfigured] = useState(false);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

    // When the parent provides a handler, the "hide already configured" filter is controlled externally.
    const isHideControlled = onHideConfiguredChange !== undefined;
    const hideConfigured = isHideControlled ? !!hideConfiguredProp : internalHideConfigured;
    const setHideConfigured = isHideControlled ? onHideConfiguredChange : setInternalHideConfigured;

    const handleSearch = (search: string) => {
        setSearch(search);
    };

    return (
        <CatalogContainer>
            <CompactSection
                description={
                    cardTitleHtml ? (
                        cardTitleHtml
                    ) : (
                        <span>{cardTitle ?? T._('Select the product from your store that you wish to make customizable via Zakeke', 'Admin')}</span>
                    )
                }
            >
                <CustomErrorBoundary
                    render={() => <NotificationBanner type='ERROR' content={T._('Failed to get the products list from your store.', 'Admin')} />}
                >
                    {!isAddSalesChannel && !isPublishingProduct && !isForImportingVariants && (
                        <SearchRow>
                            <SearchInput
                                fullWidth
                                placeholder={T._('Search by product name or ID. On Etsy also by listing ID or phrase, on Magento by SKU.', 'Admin')}
                                onSearch={handleSearch}
                            />
                            <ResourceListViewModeSwitch
                                viewMode={viewMode === 'grid' ? 'cards' : 'table'}
                                viewModeLabel={T._('View mode', 'Admin')}
                                cardsViewLabel={T._('Grid view', 'Admin')}
                                tableViewLabel={T._('List view', 'Admin')}
                                onViewModeChange={(mode) => setViewMode(mode === 'cards' ? 'grid' : 'list')}
                            />
                            <FilterDivider />
                            <SmallCheckbox>
                                <Checkbox label={T._('Hide already configured products', 'Admin')} checked={hideConfigured} onChange={setHideConfigured} />
                            </SmallCheckbox>
                        </SearchRow>
                    )}

                    {isPublishingProduct && !isHideControlled && (
                        <PublishingCheckboxRow>
                            <Checkbox label={T._('Hide already configured products', 'Admin')} checked={hideConfigured} onChange={setHideConfigured} />
                        </PublishingCheckboxRow>
                    )}

                    <Suspense fallback={<CatalogSkeleton viewMode={viewMode} />}>
                        <StoreCatalogQuery
                            salesChannelID={salesChannelID}
                            search={searchProduct || search}
                            onProductClick={onProductClick}
                            scrollContainer={scrollContainer}
                            selectedProductsSKUs={selectedProductsSKUs}
                            showSelectionCheckboxes={showSelectionCheckboxes}
                            hideProductsNotConfigured={hideProductsNotConfigured}
                            hideAlreadyConfiguredProducts={hideConfigured}
                            hideAlreadyConfiguredBadge={hideAlreadyConfiguredBadge}
                            disableProductCode={disableProductCode}
                            viewMode={viewMode}
                        />
                    </Suspense>
                </CustomErrorBoundary>
            </CompactSection>
        </CatalogContainer>
    );
};

const CatalogSkeleton: FunctionComponent<{ viewMode?: 'grid' | 'list' }> = ({ viewMode = 'grid' }) => (
    <CatalogListLoaderContent columns={viewMode === 'list' ? { xs: 1, md: 1, lg: 1 } : { xs: 1, md: 5, lg: 8 }} />
);

// Container for the query
const StoreCatalogQuery: FunctionComponent<{
    salesChannelID: string;
    search: string;
    scrollContainer?: HTMLDivElement | null;
    selectedProductsSKUs?: string[];
    showSelectionCheckboxes?: boolean;
    onProductClick: (merchantProduct: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>) => void;
    hideAlreadyConfiguredBadge?: boolean;
    hideProductsNotConfigured?: boolean;
    hideAlreadyConfiguredProducts?: boolean;
    disableProductCode?: string;
    viewMode?: 'grid' | 'list';
}> = ({
    salesChannelID,
    search,
    scrollContainer,
    selectedProductsSKUs,
    showSelectionCheckboxes,
    onProductClick,
    hideAlreadyConfiguredBadge,
    hideProductsNotConfigured,
    hideAlreadyConfiguredProducts,
    disableProductCode,
    viewMode = 'grid'
}) => {
    const query = useLazyLoadQuery<storeCatalogQuery>(
        graphql`
            query storeCatalogQuery($salesChannelID: ID!) {
                ...storeCatalogPaginationFragment__merchantProducts @arguments(salesChannelID: $salesChannelID)
            }
        `,
        {
            salesChannelID
        }
    );

    return (
        <StoreCatalogPaginationFragment
            query={query}
            search={search}
            selectedProductsSKUs={selectedProductsSKUs}
            showSelectionCheckboxes={showSelectionCheckboxes}
            onProductClick={onProductClick}
            scrollContainer={scrollContainer}
            hideProductsNotConfigured={hideProductsNotConfigured}
            hideAlreadyConfiguredProducts={hideAlreadyConfiguredProducts}
            hideAlreadyConfiguredBadge={hideAlreadyConfiguredBadge}
            disableProductCode={disableProductCode}
            columns={viewMode === 'list' ? { xs: 1, md: 1, lg: 1 } : { xs: 1, md: 5, lg: 8 }}
            viewMode={viewMode}
        />
    );
};

export default StoreCatalog;
